import { motion } from "framer-motion";

interface CityDefectProfile {
  city: string;
  type: string;
  defects: { name: string; percent: number; reason: string }[];
}

const cityProfiles: CityDefectProfile[] = [
  {
    city: "Bagnolet",
    type: "Urbain dense",
    defects: [
      { name: "Éclairage", percent: 35, reason: "circulation nocturne intense" },
      { name: "Pneus", percent: 28, reason: "nids-de-poule fréquents" },
      { name: "Freins", percent: 22, reason: "arrêts fréquents en ville" },
      { name: "Pollution", percent: 10, reason: "encrassement moteur" },
    ],
  },
  {
    city: "Vincennes",
    type: "Résidentiel + Bois",
    defects: [
      { name: "Éclairage", percent: 30, reason: "usage normal" },
      { name: "Suspension", percent: 25, reason: "ralentisseurs fréquents" },
      { name: "Pneus", percent: 22, reason: "pavés et bordures" },
      { name: "Corrosion", percent: 5, reason: "humidité du Bois" },
    ],
  },
  {
    city: "Rosny-sous-Bois",
    type: "Circulation soutenue",
    defects: [
      { name: "Éclairage", percent: 32, reason: "usage intensif" },
      { name: "Freins", percent: 26, reason: "kilométrage élevé" },
      { name: "Pneus", percent: 24, reason: "usure rapide N186" },
      { name: "Pollution", percent: 12, reason: "trajets longs" },
    ],
  },
];

const getBarColor = (percent: number) => {
  if (percent >= 30) return "bg-red-500";
  if (percent >= 20) return "bg-amber-500";
  return "bg-green-500";
};

const getEmoji = (percent: number) => {
  if (percent >= 30) return "🔴";
  if (percent >= 20) return "🟡";
  return "🟢";
};

export const CityDefectsComparison = () => {
  return (
    <section className="py-20 bg-gray-50">
      <div className="container mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <span className="text-autosur-red font-heading font-semibold tracking-widest text-sm mb-4 block">
            STATISTIQUES LOCALES
          </span>
          <h2 className="font-heading text-3xl font-bold text-autosur-navy mb-4">
            Défauts courants : comparaison par zone géographique
          </h2>
          <p className="font-body text-muted-foreground max-w-2xl mx-auto">
            Chaque zone a ses spécificités. Nos contrôleurs connaissent les défauts typiques
            de votre secteur et savent exactement quoi vérifier.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
          {cityProfiles.map((profile, index) => (
            <motion.div
              key={profile.city}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100"
            >
              <div className="mb-4">
                <h3 className="font-heading font-bold text-autosur-navy text-lg">{profile.city}</h3>
                <span className="font-body text-muted-foreground text-xs">{profile.type}</span>
              </div>
              <div className="space-y-3">
                {profile.defects.map((defect) => (
                  <div key={defect.name}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-heading text-autosur-navy text-sm font-semibold">
                        {getEmoji(defect.percent)} {defect.name}
                      </span>
                      <span className="font-heading font-bold text-autosur-red text-sm">{defect.percent}%</span>
                    </div>
                    <div className="w-full bg-gray-100 rounded-full h-2 mb-1">
                      <div
                        className={`${getBarColor(defect.percent)} rounded-full h-2 transition-all`}
                        style={{ width: `${defect.percent}%` }}
                      />
                    </div>
                    <p className="font-body text-muted-foreground text-[11px]">{defect.reason}</p>
                  </div>
                ))}
              </div>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="max-w-5xl mx-auto mt-8 bg-autosur-navy rounded-2xl p-6 text-white text-center"
        >
          <p className="font-heading font-bold">
            Nos contrôleurs connaissent les particularités de votre zone
          </p>
          <p className="font-body text-white/70 text-sm mt-1">
            20+ ans d'expérience locale • 343 avis ⭐ 4.9/5 • Résultat immédiat
          </p>
        </motion.div>
      </div>
    </section>
  );
};
